import { QueryClient } from "@tanstack/react-query";
import { ModelName, QueryMethodForModel, ParamsForMethod } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for getting query defaults.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template Method - The query method for the model
 */
export interface GetTellescopeQueryDefaultsArgs<Model extends ModelName, Method extends QueryMethodForModel<Model>> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down which defaults to get. If omitted, gets defaults for all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Gets the default options for a specific Tellescope query.
 * Returns the default query options object. If no defaults have been set, returns an empty object.
 * Query defaults are used to set default configurations like staleTime, gcTime, etc.
 *
 * @param args - The get query defaults arguments
 * @returns The default query options
 *
 * @example
 * ```tsx
 * // Get query defaults for a method
 * const defaults = getTellescopeQueryDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Get query defaults for a specific query
 * const specificDefaults = getTellescopeQueryDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * console.log("Stale time:", defaults.staleTime);
 * console.log("Cache time:", defaults.gcTime);
 * ```
 */
export const getTellescopeQueryDefaults = <Model extends ModelName, Method extends QueryMethodForModel<Model>>({
  model,
  method,
  request,
  queryClient,
}: GetTellescopeQueryDefaultsArgs<Model, Method>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.getQueryDefaults(queryKey);
};
